import { Box, Card, Typography, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { PintIcon } from 'assets/custom_icons/customIcons';
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { IIsPdf } from '../../models';
import { AvaniLightTheme } from '../../theme/schemes/AvaniLightTheme';
import { AvaniNumberSpin } from '../DataDisplay';

interface AvaniSavedPintsCardProps extends IIsPdf {
  title: string;
  savedPintsValue: number;
  text?: string;
  small?: boolean;
  sx?: any;
}

const AvaniSavedPintsStyledCard = styled(Card)(
  () => `
  border-radius: 16px;
  padding: 16px;
  position: relative;
  
  .AvaniAnimatedBackground {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 0; // Ensure it is behind other content
  }

  .MuiTypography-root.MuiTypography-h3 {
    margin: 0;
    line-height: 1;
    padding-bottom: 12px;
    display: block;
    width: 100%;
    float: left;
  }
  
   .MuiTypography-root.MuiTypography-h3, .MuiTypography-root.MuiTypography-body2 {
    position: relative;
    z-index: 1;
  }

  .MuiTypography-root.MuiTypography-h3.noText {
    padding-bottom: 0;
  }

  &.AvaniBeerCard {
    .AvaniCardContent .MuiTypography-root.MuiTypography-h3 {
      float: left;
      width: 100%;
      margin-top: 28px;
      padding-bottom: 16px;
    }
  }

  .animated-container {
    margin-top: -20px;
  }
`
);

export const AvaniSavedPintsCardBackground: FC<AvaniSavedPintsCardProps> = ({
  title,
  savedPintsValue,
  text,
  small,
  sx,
  isPDF = false
}) => {
  const theme = isPDF ? AvaniLightTheme : useTheme();
  const CardEl = useRef<HTMLDivElement>(null);

  const [svgHeight, setSvgHeight] = useState(CardEl?.current ? CardEl.current.clientHeight : 188);
  const [svgWidth, setSvgWidth] = useState(CardEl?.current ? CardEl.current.clientWidth : Math.round(svgHeight * 1.34));
  const clickTriggerRef = useRef(0);
  const [clickTrigger, setClickTrigger] = useState(0);

  const handleClickTrigger = useCallback(() => {
    clickTriggerRef.current += 1;
    setClickTrigger(clickTriggerRef.current);
  }, []);

  const numberSpinSx = useMemo(() => ({ marginTop: '12px', marginRight: '12px', position: 'relative', zIndex: 1 }), []);

  useEffect(() => {
    const onResize = () => {
      setSvgHeight(CardEl?.current ? CardEl.current.clientHeight : 188);
      setSvgWidth(CardEl?.current ? CardEl.current.clientWidth : Math.round(svgHeight * 1.34));
    };

    onResize();

    window.addEventListener('resize', onResize);

    if (!CardEl.current) {
      return window.removeEventListener('resize', onResize);
    } else {
      const resizeObserver = new ResizeObserver(() => {
        onResize();
      });

      resizeObserver.observe(CardEl.current);

      return () => {
        window.removeEventListener('resize', onResize);
        resizeObserver.disconnect();
      };
    }
    //todo:v2-changes
  }, []);

  return (
    <AvaniSavedPintsStyledCard
      ref={CardEl}
      raised={true}
      sx={{
        height: '100%',
        backgroundColor: theme.colors.avani.lightTurc,
        ['.SavedBeerSpinner']: { textAlign: 'right', marginTop: 0 },
        ...sx
      }}
    >
      <div className="AvaniAnimatedBackground">
        <svg
          width={svgWidth}
          height={svgHeight}
          viewBox={`0 0 ${svgWidth} ${svgHeight}`}
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d={`M0 ${svgHeight * 0.55} 
      C 0 ${svgHeight * 0.1}, 
        ${svgWidth} ${svgHeight * 0.55}, 
        ${svgWidth} ${svgHeight * 0.15} 
      C ${svgWidth} ${svgHeight * -0.2}, 
        ${svgWidth} ${svgHeight * 0.65}, 
        ${svgWidth} ${svgHeight * 0.95} 
      V ${svgHeight} 
      H0 
      Z`}
            fill={theme.colors.avani.aYellow}
          />
        </svg>
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          height: '100%',
          position: 'relative',
          zIndex: 1
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            textAlign: 'center',
            position: 'relative',
            zIndex: 1
          }}
        >
          <AvaniNumberSpin
            numberValue={savedPintsValue}
            fSize={small ? 40 : 50}
            sx={numberSpinSx}
            isPDF={isPDF}
            clickTrigger={clickTrigger}
            onClickTrigger={handleClickTrigger}
          />
          <Box>{PintIcon({ width: 42, height: 42, isPDF })}</Box>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', marginBottom: 10 }}>
          <Typography variant="h3" className={!text ? 'noText' : ''} color={theme.colors.avani.teal}>
            {title}
          </Typography>
          {text && (
            <Typography variant="body2" fontSize={14} lineHeight={1}>
              {text}
            </Typography>
          )}
        </div>
      </div>
    </AvaniSavedPintsStyledCard>
  );
};
